import { Text } from '@chakra-ui/react';

export const SUCCESS_MESSAGE = 'Success.';
export const CSV_TYPE = 'text/csv';
export const CSV_TYPE_ERROR = 'File must be .csv type';
export const ERROR_MESSAGE = 'Something went wrong. Please try again later.';
export const TEMPLATE_FILENAME = 'NumberBlocksUpdateTemplate.csv';
export const INVALID_FILENAME = 'InvalidNumberBlocks.csv';
export const FAILED_FILENAME = 'FailedNumberBlocks.csv';

export const FIELDS_TO_EXPORT = {
    id: 'Block ID',
    first: 'First Number',
    last: 'Last Number',
    blockSize: 'Block Size',
    partnerID: 'Partner ID',
    companyID: 'Company ID',
    companyName: 'Company Name',
    inboundCarrier: 'Inbound Carrier ID',
    outboundCarrier: 'Outbound Carrier ID',
    publicPool: 'Public Pool',
    delete: 'Delete',
};

export const FIELDS_WITH_ERRORS = {
    ...FIELDS_TO_EXPORT,
    errors: 'Errors',
    partnerName: 'Partner Name',
    companyName: 'Company Name',
    inboundCarrierName: 'Inbound Carrier Name',
    outboundCarrierName: 'Outbound Carrier Name',
    first: 'First Number',
    last: 'Last Number',
    blockSize: 'Block Size',
};

export const API_RESULT_FIELDS_TO_EXPORT = {
    ...FIELDS_TO_EXPORT,
    first: 'First Number',
    last: 'Last Number',
    apiErrors: 'Errors',
};

export const VERIFICATION_CHECKS = [
    {
        label: 'Received CSV',
        check: false,
        current: true,
        completionCounter: 0,
        total: 1,
    },
    {
        label: 'Fetching Lookup Data',
        check: false,
        current: false,
        completionCounter: 0,
        total: 3,
    },
    {
        label: 'Converting CSV',
        check: false,
        current: false,
        completionCounter: 0,
        total: 2,
    },
    {
        label: 'Verifying Number Blocks',
        check: false,
        current: false,
        completionCounter: 0,
        total: 2,
    },
    {
        label: 'Validating Partners & Companies',
        check: false,
        current: false,
        completionCounter: 0,
        total: 2,
    },
    {
        label: 'Validating Carriers',
        check: false,
        current: false,
        completionCounter: 0,
        total: 1,
    },
];

export const DEFAULT_VERIFICATION_RESULT = {
    passed: {
        columns: [],
        data: [],
    },
    failed: {
        columns: [],
        data: [],
    },
};

export const haveErrorsFilter = (v) => v.errors?.length > 0;

// Error messages
export const BLOCK_DNE_ERROR = 'Number block does not exist';
export const INVALID_PARTNER_ERROR = 'Invalid Partner ID';
export const INVALID_COMPANY_ERROR = 'Invalid Company ID';
export const INVALID_CARRIER_ERROR = 'Invalid Carrier ID';
export const COMPANY_MISMATCH_ERROR = 'Company does not belong to specified partner';
export const CANNOT_DELETE_WITH_COMPANY_ERROR = 'Cannot delete block that is still assigned to a company. Set Company ID to empty first.';
export const CANNOT_DELETE_WHILE_REMOVING_COMPANY_ERROR = 'Cannot delete block while removing company association. Remove company in a separate operation first, then delete.';
export const CANNOT_DELETE_AND_CHANGE_COMPANY_ERROR = 'Cannot delete and change company simultaneously. Remove company first, then delete in a separate operation.';
export const INVALID_PUBLIC_POOL_ERROR = 'Public Pool must be TRUE or FALSE';
export const INVALID_DELETE_ERROR = 'Delete must be FALSE (deletion not supported via CSV)';
export const NO_CHANGES_ERROR = 'No changes detected';
export const READ_ONLY_FIELD_MODIFIED_ERROR = 'First Number, Last Number, and Block Size are read-only and cannot be modified or deleted';

export const getInstructions = () => {
    return (
        <>
            <Text mb={2}>
                <strong>Step 2 - Choose Export Type</strong>
            </Text>
            <Text mb={3}>
                Select which number blocks to export. You will update the CSV and re-upload in Step 3.
            </Text>
        </>
    );
};

export const passedColumns = [
    {
        field: 'id',
        headerName: 'Block ID',
        flex: 0.8,
    },
    {
        field: 'first',
        headerName: 'First Number',
        flex: 1,
    },
    {
        field: 'last',
        headerName: 'Last Number',
        flex: 1,
    },
    {
        field: 'blockSize',
        headerName: 'Block Size',
        flex: 0.6,
    },
    // {
    //     field: 'partnerName',
    //     headerName: 'Partner',
    //     flex: 1,
    // },
    {
        field: 'companyID',
        headerName: 'Company',
        flex: 1,
    },
    {
        field: 'inboundCarrier',
        headerName: 'Inbound Carrier',
        flex: 1,
    },
    {
        field: 'outboundCarrier',
        headerName: 'Outbound Carrier',
        flex: 1,
    },
    {
        field: 'publicPool',
        headerName: 'Public Pool',
        flex: 0.6,
        cellDataType: 'boolean',
    },
    {
        field: 'delete',
        headerName: 'Delete',
        flex: 0.6,
        cellDataType: 'boolean',
    },
];

export const failedColumns = [
    ...passedColumns,
    {
        field: 'errors',
        headerName: 'Errors',
        flex: 1.5,
        wrapText: true,
        autoHeight: true,
        cellRenderer: (params) => {
            const errors = params.value;
            if (Array.isArray(errors) && errors.length > 0) {
                return (
                    <div style={{ whiteSpace: 'pre-wrap', padding: '4px 0' }}>
                        {errors.join('\n')}
                    </div>
                );
            }
            return '';
        },
    },
];

export const succeedAPIColumns = [
    {
        field: 'id',
        headerName: 'Block ID',
        flex: 0.8,
    },
    {
        field: 'companyID',
        headerName: 'Company ID',
        flex: 1,
    },
    {
        field: 'delete',
        headerName: 'Delete',
        flex: 0.6,
        cellDataType: 'boolean',
    },
    {
        field: 'result',
        headerName: 'Result',
        flex: 1,
        valueGetter: (params) => {
            return params.data?.delete ? 'Deleted' : 'Successfully updated';
        },
    },
];

export const failedAPIColumns = [
    {
        field: 'id',
        headerName: 'Block ID',
        flex: 0.8,
    },
    {
        field: 'delete',
        headerName: 'Delete',
        flex: 0.6,
        cellDataType: 'boolean',
    },
    {
        field: 'apiErrors',
        headerName: 'Errors',
        flex: 2,
        wrapText: true,
        autoHeight: true,
        cellRenderer: (params) => {
            const error = params.value;
            if (error) {
                return (
                    <div style={{ whiteSpace: 'pre-wrap', padding: '4px 0', color: 'red' }}>
                        {error}
                    </div>
                );
            }
            return '';
        },
    },
];
